import React, { useState, useEffect } from 'react';
import { ArrowRight, Play, Users, Calendar, Mail, Shield, DollarSign, Calculator } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import favicon from '/public/favicon.svg';
import demoThumbnail from '/public/lovable-uploads/munger-demo-thumbnail.jpg';

interface SdrRoiHeroSectionProps {
  onStartClick?: () => void;
}

const SdrRoiHeroSection: React.FC<SdrRoiHeroSectionProps> = ({ onStartClick }) => {
  const [showVideo, setShowVideo] = useState(false);
  const [savingsCounter, setSavingsCounter] = useState(8203);
  const navigate = useNavigate();

  // Increment savings counter periodically
  useEffect(() => {
    const interval = setInterval(() => {
      setSavingsCounter(prev => prev + Math.floor(Math.random() * 5) + 1);
    }, 3000);
    
    return () => clearInterval(interval);
  }, []);

  // Handler for sign in
  const handleSignIn = () => {
    navigate('/login');
  };

  // Sticky navigation with dual CTAs
  const StickyNav = () => (
    <div className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex justify-between items-center">
        <div className="flex items-center">
          <img 
            src={favicon} 
            alt="Munger.ai" 
            className="h-8 w-8 mr-2"
          />
          <span className="font-bold text-lg sm:text-xl text-gray-900">Munger.ai</span>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <Button 
            variant="ghost" 
            size="sm"
            onClick={handleSignIn}
            className="text-gray-600 hover:text-gray-900 text-xs sm:text-sm px-3 sm:px-4"
          >
            Sign In
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => navigate('/book-demo')}
            className="border-gray-300 hover:border-gray-400 text-xs sm:text-sm px-3 sm:px-4"
          >
            <span className="hidden sm:inline">Book </span>Demo
          </Button>
          <Button 
            size="sm" 
            className="bg-blue-600 hover:bg-blue-700 text-xs sm:text-sm px-3 sm:px-4" 
            onClick={onStartClick}
          >
            Start Free<span className="hidden sm:inline"> →</span>
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="relative overflow-hidden bg-gradient-to-b from-green-50 to-blue-50">
      {/* Sticky Navigation */}
      <StickyNav />

      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-green-500/5 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-500/5 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 pt-24 pb-20">
        <div className="text-center">
          {/* Cost indicators */}
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-wrap justify-center gap-2 sm:gap-4 mb-8"
          >
            <Badge className="bg-green-100 text-green-700 border-green-200 text-xs sm:text-sm">
              <DollarSign className="w-3 h-3 mr-1" />
              ${savingsCounter.toLocaleString()} saved this month
            </Badge>
            <Badge className="bg-blue-100 text-blue-700 border-blue-200 text-xs sm:text-sm">
              <Calculator className="w-3 h-3 mr-1" />
              986x ROI
            </Badge>
          </motion.div>

          {/* Main content wrapper */}
          <div className="relative">
            {/* Clear value proposition */}
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-4xl sm:text-5xl md:text-7xl font-bold text-gray-900 mb-6 leading-tight px-4"
            >
              Your SDR Costs $96,000/Year
            </motion.h1>

            {/* Supporting copy - concrete benefits */}
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-lg sm:text-xl md:text-2xl text-gray-600 mb-8 max-w-4xl mx-auto px-4"
            >
              We Cost $97/Month. Same 50 meetings. 986x less money.
            </motion.p>

            {/* Cost comparison table */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="max-w-3xl mx-auto mb-12 bg-white rounded-xl shadow-lg overflow-hidden"
            >
              <div className="grid grid-cols-3 text-left">
                <div className="p-4 font-medium text-gray-500 bg-gray-50">
                  <span className="sr-only">Feature</span>
                </div>
                <div className="p-4 font-semibold text-center text-gray-900 bg-gray-50">
                  SDR
                </div>
                <div className="p-4 font-semibold text-center text-blue-600 bg-blue-50">
                  Munger.ai
                </div>
                
                {/* Cost Row */}
                <div className="p-4 border-t border-gray-200 font-medium">
                  Monthly Cost
                </div>
                <div className="p-4 border-t border-gray-200 text-center">
                  $8,000
                </div>
                <div className="p-4 border-t border-gray-200 text-center font-bold text-green-600">
                  $97
                </div>
                
                {/* Meetings Row */}
                <div className="p-4 border-t border-gray-200 font-medium">
                  Monthly Meetings
                </div>
                <div className="p-4 border-t border-gray-200 text-center">
                  ~50
                </div>
                <div className="p-4 border-t border-gray-200 text-center">
                  50+
                </div>
                
                {/* Hours Row */}
                <div className="p-4 border-t border-gray-200 font-medium">
                  Hours Required
                </div>
                <div className="p-4 border-t border-gray-200 text-center">
                  160+
                </div>
                <div className="p-4 border-t border-gray-200 text-center font-bold text-green-600">
                  0
                </div>
                
                {/* Training Row */}
                <div className="p-4 border-t border-gray-200 font-medium">
                  Training Required
                </div>
                <div className="p-4 border-t border-gray-200 text-center">
                  Weeks
                </div>
                <div className="p-4 border-t border-gray-200 text-center font-bold text-green-600">
                  None
                </div>
              </div>
            </motion.div>

            {/* Dual CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8"
            >
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg" 
                  onClick={onStartClick}
                  className="bg-green-600 hover:bg-green-700 text-white px-8 py-6 text-lg rounded-lg shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
                >
                  Calculate Your Savings →
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  onClick={() => navigate('/book-demo')}
                  className="border-2 border-gray-300 hover:border-gray-400 px-8 py-6 text-lg rounded-lg"
                >
                  Book Demo
                  <Calendar className="ml-2 h-5 w-5" />
                </Button>
              </div>
            </motion.div>

            {/* Trust indicators */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
              className="text-xs sm:text-sm text-gray-600 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4"
            >
              <span>✅ 30-day money-back guarantee</span>
              <span className="hidden sm:inline">✅ Setup in under 10 minutes</span>
              <span>✅ Cancel anytime</span>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SdrRoiHeroSection; 